import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import { QueryState } from "@/components/common/AsyncState";
import { fetchIntegrationSettings, updateIntegrationSettings } from "@/api/settings";
import { DUE_DAY_MODE_LABELS } from "@/types/enums";
import type { DueDayMode } from "@/types/enums";
import type { IntegrationSettings } from "@/types/settings";

const GRADE_SCALE_OPTIONS = [5, 10, 100];

/**
 * Вкладка «Система» (карточка «Учебный процесс и оплата» прототипа, ТЗ §2.13):
 * название центра, размер пакета уроков, день оплаты, скидка за второй
 * предмет/члена семьи, шкала оценок, признак «вести оценки и ДЗ», список
 * временных слотов расписания.
 */
export function SystemTab() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["integration-settings"], queryFn: fetchIntegrationSettings });
  const [form, setForm] = useState<IntegrationSettings | null>(null);
  const [slotsText, setSlotsText] = useState("");
  const [snackbar, setSnackbar] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setForm(data);
      setSlotsText(data.timeSlots.join("\n"));
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload: Partial<IntegrationSettings>) => updateIntegrationSettings(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["integration-settings"] });
      setSnackbar("Настройки сохранены.");
    },
  });

  function set<K extends keyof IntegrationSettings>(key: K, value: IntegrationSettings[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  function save() {
    if (!form) return;
    const timeSlots = slotsText.split("\n").map((line) => line.trim()).filter(Boolean);
    mutation.mutate({ ...form, timeSlots });
  }

  return (
    <QueryState isLoading={isLoading} error={error}>
      {form && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Учебный процесс и оплата
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Название центра" fullWidth value={form.centerName} onChange={(e) => set("centerName", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Уроков в пакете"
                  type="number"
                  fullWidth
                  value={form.lessonsPerPack}
                  onChange={(e) => set("lessonsPerPack", Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="День оплаты" fullWidth value={form.dueDayMode} onChange={(e) => set("dueDayMode", e.target.value as DueDayMode)}>
                  {Object.entries(DUE_DAY_MODE_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Скидка за второй предмет / члена семьи, сум"
                  type="number"
                  fullWidth
                  value={form.familyDiscount}
                  onChange={(e) => set("familyDiscount", Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Срок отсрочки долга, дней"
                  type="number"
                  fullWidth
                  value={form.graceDays}
                  onChange={(e) => set("graceDays", Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Шкала оценок" fullWidth value={form.gradeScale} onChange={(e) => set("gradeScale", Number(e.target.value))}>
                  {GRADE_SCALE_OPTIONS.map((scale) => (
                    <MenuItem key={scale} value={scale}>
                      1–{scale}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.gradesOn} onChange={(e) => set("gradesOn", e.target.checked)} />}
                  label="Вести оценки и домашние задания"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Временные слоты (по одному в строке)"
                  fullWidth
                  multiline
                  minRows={6}
                  value={slotsText}
                  onChange={(e) => setSlotsText(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>

          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" disabled={mutation.isPending} onClick={save}>
              Сохранить
            </Button>
          </Stack>
        </Stack>
      )}
      <Snackbar open={Boolean(snackbar)} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar} />
    </QueryState>
  );
}
